/**
 * A checklist item's note and photos, under its answer.
 *
 * Either can be added to any item. An answer that asks for one opens it and
 * marks it required; a save without it is refused and the reason shows
 * here. On a phone the photo picker offers the camera or the library. The
 * values live in the section's form, so they save with its answers.
 */

import { useState, type Dispatch, type SetStateAction } from 'react'
import { useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Camera, StickyNote } from 'lucide-react'

import { Button } from '@/shared/components/ui/button'
import { FileUploader } from '@/shared/components/ui/file-uploader'
import { Label } from '@/shared/components/ui/label'
import { Textarea } from '@/shared/components/ui/textarea'
import { toastApiError } from '@/shared/lib/api-error'
import { compressImage } from '@/shared/lib/compress-image'
import { toUploadedFileItem, type UploadedFileItem } from '@/shared/lib/file-types'
import { useFieldState, useUpload } from '@/shared/lib/forms/form-context'
import type { ChecklistQuestion } from '@/admin/hooks/use-inspections'

/** The form field holding an item's note and photos is its code plus this. */
export const EXTRAS = '__extras'

/** As long as a saved note may be. */
const MAX_NOTE_CHARS = 5000

export interface ItemExtras {
  note: string
  photos: UploadedFileItem[]
}

export function ChecklistItemExtras({
  question,
  onUploading,
}: {
  question: ChecklistQuestion
  /** Counts the photos uploading, so the section waits for them before it saves. */
  onUploading: Dispatch<SetStateAction<number>>
}) {
  const { t } = useTranslation()
  const name = `${question.code_name}${EXTRAS}`
  const { value, error, setValue, clearError } = useFieldState(name)
  const answer: unknown = useWatch({ name: question.code_name })
  const uploadFile = useUpload()
  const [opened, setOpened] = useState({ note: false, photos: false })
  const extras = (value as ItemExtras | null) ?? { note: '', photos: [] }

  // What the picked answer asks for.
  const picked = Array.isArray(answer) ? answer : [answer]
  const needs = new Set(
    question.options
      .filter((option) => picked.includes(option.value))
      .flatMap((option) => (Array.isArray(option.requires) ? option.requires : [])),
  )
  const showNote = opened.note || needs.has('note') || extras.note !== ''
  const showPhotos = opened.photos || needs.has('photo') || extras.photos.length > 0

  const update = (next: Partial<ItemExtras>) => {
    setValue({ ...extras, ...next })
    clearError()
  }
  const upload = async (file: File): Promise<UploadedFileItem> => {
    if (!uploadFile) throw new Error('No file upload handler configured')
    onUploading((n) => n + 1)
    try {
      const photo = await compressImage(file).catch(() => file)
      const result = await uploadFile({
        file: photo,
        use_case: 'INSPECTION_EVIDENCE',
        fieldName: name,
      })
      return toUploadedFileItem(result, { name: photo.name, type: photo.type })
    } catch (err) {
      toastApiError(err, t('inspections.checklist.uploadFailed', 'The photo could not be uploaded.'))
      throw err
    } finally {
      onUploading((n) => n - 1)
    }
  }

  return (
    <div className="flex flex-col gap-3" data-testid={`checklist-extras-${question.code_name}`}>
      {!showNote || !showPhotos ? (
        <div className="-ml-2 flex flex-wrap gap-1">
          {!showNote ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-9"
              onClick={() => setOpened((o) => ({ ...o, note: true }))}
            >
              <StickyNote size={14} />
              {t('inspections.checklist.addNote', 'Add a note')}
            </Button>
          ) : null}
          {!showPhotos ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-9"
              onClick={() => setOpened((o) => ({ ...o, photos: true }))}
            >
              <Camera size={14} />
              {t('inspections.checklist.addPhotos', 'Add photos')}
            </Button>
          ) : null}
        </div>
      ) : null}
      {showNote ? (
        <div className="flex flex-col gap-1.5">
          <ItemLabel htmlFor={`${name}-note`} required={needs.has('note')}>
            {t('inspections.checklist.note', 'Note')}
          </ItemLabel>
          <Textarea
            id={`${name}-note`}
            aria-required={needs.has('note')}
            value={extras.note}
            maxLength={MAX_NOTE_CHARS}
            rows={3}
            onChange={(event) => update({ note: event.target.value })}
          />
        </div>
      ) : null}
      {showPhotos ? (
        <div className="flex flex-col gap-1.5">
          <ItemLabel required={needs.has('photo')}>
            {t('inspections.checklist.photos', 'Photos')}
          </ItemLabel>
          <FileUploader
            value={extras.photos}
            onChange={(photos) => update({ photos })}
            uploadFn={upload}
            multiple
            accept="image/*"
            hint={t('inspections.checklist.photosHint', 'Take or choose photos')}
            disabled={!uploadFile}
            error={!!error}
          />
        </div>
      ) : null}
      {error ? <p className="text-[11px] text-[var(--status-error)]">{error}</p> : null}
    </div>
  )
}

/** A label with the required mark the shared form fields use. */
function ItemLabel({
  children,
  required,
  htmlFor,
}: {
  children: string
  required: boolean
  htmlFor?: string
}) {
  return (
    <Label htmlFor={htmlFor} className="clara-label font-normal">
      <span className="inline-flex items-center gap-[2px]">
        <span>{children}</span>
        {required ? (
          <span
            aria-hidden="true"
            className="mt-2 text-[17px] leading-[4px] text-[var(--status-error)]"
          >
            *
          </span>
        ) : null}
      </span>
    </Label>
  )
}
